import { adapters, UnhandledHttpResponseError } from '@garner-health/api-client';
import { getAuth } from '@garner-health/components-auth';
import constants from 'app-constants';

import createLogger from '~/logging';

export const baseClientOptions = { baseUrl: constants.fullApiBaseUrl, adapter: adapters.fetch };
// The gateway sends `/demo/...` to the demo services by URL alone, so demo routing doesn't depend on a token scope claim.
export const demoClientOptions = { ...baseClientOptions, baseUrl: `${constants.fullApiBaseUrl}/demo` };
const log = createLogger(__filename, { component: 'error-boundary' });

export async function getDefaultHeaders(): Promise<Record<string, string>> {
  try {
    const auth = getAuth();
    const token = await auth.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      'Garner-Application-Version': GARNER_VERSION,
      'Garner-Application-ID': 'ui-datapro',
    };
  } catch (err) {
    log.error({ err }, 'Error getting JWT');
    return {};
  }
}

/**
 * The 429 is produced by the Istio gateway, not the service, so it is absent from most operations'
 * generated status maps and `.onStatus(429, ...)` will not type-check. Inspect the status of the
 * error that reaches `.catch` instead — the same approach `care-goals.ts` uses for its 503.
 */
export function isRateLimitedError(err: unknown): err is UnhandledHttpResponseError {
  return err instanceof UnhandledHttpResponseError && err.response.status === 429;
}

/**
 * Reads the rate-limit reset header from a throttled response. Envoy emits `x-ratelimit-reset` or
 * `ratelimit-reset` depending on its ratelimit draft version, so probe both. Logged rather than
 * shown to the user: the search EnvoyFilter does not set `enable_x_ratelimit_headers` yet, so this
 * is how we confirm whether the header ever arrives (FC-1832).
 */
export function getRateLimitReset(headers: Partial<Record<string, string>>): string | undefined {
  return headers['ratelimit-reset'] ?? headers['x-ratelimit-reset'];
}

/**
 * Turn an array into multiple arrays of a maximum specified size
 * @param array array to split up
 * @param maxChunkSize max size of each resulting chunk
 */
export function chunkArray<T>(array: T[], maxChunkSize: number): T[][] {
  return Array.from({ length: Math.ceil(array.length / maxChunkSize) }, (_, index) =>
    array.slice(index * maxChunkSize, (index + 1) * maxChunkSize),
  );
}
